import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, Select, useFetch } from "../../components/crm/ui";
import { Plus, Trash2, Edit, MapPin } from "lucide-react";
import { INDIAN_STATES } from "../../lib/indianStates";
import { BENEFIT_KEYS, BENEFIT_KEY_LABELS_EN, CENTRAL_SUBSIDY_AMOUNT } from "../../lib/flyerI18n";

const EMPTY = { state: INDIAN_STATES[0], state_topup_amount: 0, benefit_keys: [...BENEFIT_KEYS] };

export default function StateBenefits() {
  const { data: rows, reload } = useFetch("/state-benefits");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const usedStates = new Set((rows || []).map((r) => r.state));

  function startCreate() {
    const next = INDIAN_STATES.find((s) => !usedStates.has(s)) || INDIAN_STATES[0];
    setForm({ ...EMPTY, state: next });
    setEditingId(null);
    setOpen(true);
  }
  function startEdit(r) {
    setForm({ state: r.state, state_topup_amount: r.state_topup_amount || 0, benefit_keys: r.benefit_keys || [] });
    setEditingId(r.id);
    setOpen(true);
  }

  function toggleBenefit(key) {
    setForm((f) => ({
      ...f,
      benefit_keys: f.benefit_keys.includes(key) ? f.benefit_keys.filter((k) => k !== key) : [...f.benefit_keys, key],
    }));
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, state_topup_amount: Number(form.state_topup_amount) || 0 };
    try {
      if (editingId) await api.put(`/state-benefits/${editingId}`, payload);
      else await api.post("/state-benefits", payload);
      toast.success(editingId ? "Updated" : "Created");
      setOpen(false); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  async function remove(r) {
    if (!window.confirm(`Delete benefits for ${r.state}?`)) return;
    await api.delete(`/state-benefits/${r.id}`);
    toast.success("Deleted"); reload();
  }

  return (
    <div data-testid="state-benefits-page">
      <PageHeader
        overline="Master Data · SC Marketing Flyer"
        title="State Benefits"
        action={<PrimaryButton onClick={startCreate} testid="create-state-benefit-btn"><Plus className="w-4 h-4" /> New State</PrimaryButton>}
      />
      <p className="text-sm text-[#4A5568] -mt-3 mb-4">Every state gets the central PM Surya Ghar Yojana subsidy (₹{CENTRAL_SUBSIDY_AMOUNT.toLocaleString("en-IN")}) automatically. Add a state top-up here ONLY if that state government layers its own additional incentive on top — the total shown on the SC's flyer is calculated for you.</p>

      <div className="bg-white border border-[#DDE5D8]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="state-benefits-table">
            <thead className="bg-[#F4F6F1] border-b border-[#DDE5D8]">
              <tr>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">State</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Total Subsidy</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Benefits</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((r) => (
                <tr key={r.id} data-testid={`state-benefit-row-${r.id}`} className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1]">
                  <td className="px-4 py-3 font-display font-semibold flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#94A3B8]" />{r.state}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-[#1F4E2C]">
                    ₹{Number(r.subsidy_amount).toLocaleString("en-IN")}
                    {r.state_topup_amount > 0 && (
                      <div className="text-[10px] font-normal text-[#94A3B8] mt-0.5">₹{CENTRAL_SUBSIDY_AMOUNT.toLocaleString("en-IN")} central + ₹{Number(r.state_topup_amount).toLocaleString("en-IN")} state top-up</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[#4A5568]">{(r.benefit_keys || []).map((k) => BENEFIT_KEY_LABELS_EN[k] || k).join(" · ") || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => startEdit(r)} data-testid={`edit-state-benefit-${r.id}`} className="p-1 text-[#475569] hover:text-[#1F4E2C]"><Edit className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(r)} data-testid={`del-state-benefit-${r.id}`} className="p-1 text-[#475569] hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {(!rows || rows.length === 0) && <tr><td colSpan={4} className="px-4 py-12 text-center text-sm font-mono text-[#94A3B8]">No state benefits configured yet. SCs will see the default India-wide benefit set until you add their state here.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? `Edit · ${form.state}` : "New State Benefit"}>
        <form onSubmit={save} className="space-y-4" data-testid="state-benefit-form">
          <Field label="State *">
            <Select value={form.state} disabled={!!editingId} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="sb-state">
              {INDIAN_STATES.map((s) => <option key={s} value={s} disabled={usedStates.has(s) && s !== form.state}>{s}</option>)}
            </Select>
          </Field>
          <Field label="State Top-up (₹)">
            <TextInput type="number" min={0} value={form.state_topup_amount} onChange={(e) => setForm({ ...form, state_topup_amount: e.target.value })} data-testid="sb-subsidy" />
            <p className="text-xs text-[#94A3B8] mt-1.5">This state's OWN additional incentive, on top of the central ₹{CENTRAL_SUBSIDY_AMOUNT.toLocaleString("en-IN")}. Leave at 0 if this state has no extra scheme. Total shown to the customer: <span className="font-semibold text-[#1F4E2C]">₹{(CENTRAL_SUBSIDY_AMOUNT + (Number(form.state_topup_amount) || 0)).toLocaleString("en-IN")}</span></p>
          </Field>
          <Field label="Applicable Benefits">
            <div className="grid grid-cols-1 gap-2 mt-1">
              {BENEFIT_KEYS.map((k) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`sb-benefit-${k}`}>
                  <input type="checkbox" checked={form.benefit_keys.includes(k)} onChange={() => toggleBenefit(k)} className="w-4 h-4" />
                  {BENEFIT_KEY_LABELS_EN[k]}
                </label>
              ))}
            </div>
          </Field>
          <div className="flex justify-end gap-3 pt-2 border-t border-[#E2E4DC]">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#475569]">Cancel</button>
            <PrimaryButton type="submit" disabled={saving} testid="save-state-benefit-btn">{saving ? "Saving…" : (editingId ? "Update" : "Create")}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
